/* eslint-disable @typescript-eslint/no-explicit-any */

import clsx from 'clsx'
import { Show } from '@/components/show'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { TableConfig } from '@/types/table/table-config'
import { dataToTableCell } from '@/utils/data-to-table-cell'
import { getCellTextStyle } from '@/utils/get-cell-text-style'
import { getNestedValue } from '@/utils/get-nested-value'

interface SimpleTableProps {
  tableConfig: TableConfig<any>
  selectable: boolean
  selectableMax?: number
  idColumn?: string
  selectedItems?: any[]
  setSelectedItems?: (item: any) => void
  selectIcon?: 'circle' | 'square'
}

export const SimpleTable = ({
  tableConfig,
  selectable,
  selectedItems,
  selectableMax,
  selectIcon = 'circle',
  setSelectedItems
}: SimpleTableProps) => {
  const handleSelect = (item: any) => {
    const idField = tableConfig.idFieldKey
    if (idField && selectedItems && setSelectedItems) {
      const found = selectedItems.findIndex((selectable) => {
        return selectable[idField] === item[idField]
      })

      if (found > -1) {
        const newSelectedItems = selectedItems.filter(
          (selectable) => selectable[idField] !== item[idField]
        )
        setSelectedItems(newSelectedItems)
      } else if (
        (selectableMax && selectedItems.length < selectableMax)
        || !selectableMax
      ) {
        setSelectedItems((prev: any) => [...prev, item])
      } else {
        setSelectedItems([item])
      }
    }
  }

  const itemIsSelected = (currentItem: any) => {
    const idField = tableConfig.idFieldKey
    if (idField && selectedItems) {
      return selectedItems.find(
        (item: any) => item[idField] === currentItem.data[idField]
      )
    }
  }

  return (
    <Table className="px-1 table-fixed">
      <TableHeader>
        <TableRow>
          <Show when={selectable}>
            <TableHead className="w-12"></TableHead>
          </Show>
          {tableConfig.heads.map((head, index) => (
            <TableHead
              className="px-4"
              key={`simple-table-${index}`}
              style={{ width: head.width || 'auto' }}
            >
              {head.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {tableConfig.data.map((data, rowIndex) => {
          const isSelected = itemIsSelected(data)
          return (
            <TableRow
              key={`simple-row-${rowIndex}`}
              className={clsx('shadow-table select-none', {
                'cursor-pointer': selectable,
                'outline outline-[var(--brand-blue-2)]': isSelected
              })}
              onClick={() => handleSelect({ ...data.data, button: undefined })}
            >
              <Show when={selectable}>
                <TableCell className="font-medium px-6 truncate">
                  <div
                    className={clsx(
                      'w-3 h-3 border border-[var(--brand-blue-2)]',
                      { 'bg-[var(--brand-blue-3)]': isSelected },
                      { 'rounded-full': selectIcon === 'circle' },
                      { 'rounded-none': selectIcon === 'square' }
                    )}
                  ></div>
                </TableCell>
              </Show>
              {tableConfig.heads.map((head, cellIndex) => {
                const value = getNestedValue(data.data, head.key)
                const style = getCellTextStyle(head, value)
                const label = dataToTableCell(head, value)
                return (
                  <TableCell
                    key={`simple-cell-${cellIndex}`}
                    className={cn('truncate px-4', style)}
                  >
                    {label}
                  </TableCell>
                )
              })}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
